import type {
  AttributesEntity,
  ValuesEntity,
} from '@eliadmin/core/src/api/products/types/product-types';
import { AttributeCheckbox } from '@eliadmin/core/src/components/categories/attributes/attribute-checkbox';
import { AttributeColor } from '@eliadmin/core/src/components/categories/attributes/attribute-color';
import {
  AttributeGeneric,
  type GenericType,
} from '@eliadmin/core/src/components/categories/attributes/attribute-generic';
import { AttributePrice } from '@eliadmin/core/src/components/categories/attributes/attribute-price';
import { AttributeRadio } from '@eliadmin/core/src/components/categories/attributes/attribute-radio';
import { AttributeSelect } from '@eliadmin/core/src/components/categories/attributes/attribute-select';
import { AttributeSubCategory } from '@eliadmin/core/src/components/categories/attributes/attribute-subcategory';
import { AttributeSupplierLocation } from '@eliadmin/core/src/components/categories/attributes/attribute-supplier-location';
import { Card } from '@eliadmin/core/src/components/ui/card';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@eliadmin/core/src/components/ui/collapsible';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@eliadmin/core/src/components/ui/popover';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { AttributeType, useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { MdKeyboardArrowDown } from 'react-icons/md';

export function AttributeSide({
  type,
  name,
  attribute,
  collapsible = true,
  className,
}: {
  type: 'subCategory' | 'generic' | 'price' | 'supplierLocation';
  name: string;
  attribute: GenericType;
  collapsible?: boolean;
  order?: number;
  className?: {
    wrapper?: string;
    trigger?: string;
  };
}) {
  const { isOpen, setIsOpen } = useDisclosure(false);

  return (
    <Card className={cn('rounded-md bg-transparent', 'ea-attribute-side-card', className?.wrapper)}>
      <Collapsible
        className={cn('flex flex-col space-y-4 px-4', 'ea-attribute-side-card__collapsible')}
        open={isOpen}
        onOpenChange={collapsible ? setIsOpen : undefined}
      >
        <CollapsibleTrigger
          className={cn(
            'flex flex-row items-center gap-2',
            'ea-attribute-side-card__collapsible-trigger',
            className?.trigger,
            { 'pointer-events-none': !collapsible },
          )}
        >
          {collapsible && (
            <span
              className={cn(
                'text-primary transition-transform duration-100 ease-linear',
                isOpen && 'rotate-90',
              )}
            >
              <ChevronRight />
            </span>
          )}
          <span className={cn(isOpen ? 'text-primary' : 'text-foreground')}>{name}</span>
        </CollapsibleTrigger>
        <CollapsibleContent
          className={cn(
            'ml-0 px-6',
            'ea-attribute-side-card__collapsible-content',
            type === 'supplierLocation' && 'px-0',
          )}
        >
          {type === 'subCategory' && <AttributeSubCategory />}
          {type === 'generic' && <AttributeGeneric type={attribute.type} type2={attribute.type2} />}
          {type === 'price' && <AttributePrice />}
          {type === 'supplierLocation' && <AttributeSupplierLocation />}
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export function AttributeSideDynamic({
  name,
  attribute,
  collapsible = true,
  order = 0,
  className,
}: {
  name: string;
  attribute: AttributesEntity;
  collapsible?: boolean;
  order?: number;
  className?: {
    wrapper?: string;
    trigger?: string;
  };
}) {
  const { isOpen, setIsOpen } = useDisclosure(collapsible ? attribute.collapse : false);
  // const updateSelectedValues = useVariantsStore(state => state.updateSelectedValue);
  // const show = collapsible ? isOpen : true;
  const filter = useCategoryStore(state => state.filter.attributes);

  const updateVariantValues = (attribute: AttributesEntity, value: ValuesEntity) => {
    const filterAttribute = filter.find(item => item.id === attribute.id);

    if (filterAttribute) {
      if (filterAttribute.values.includes(value.id)) {
        // updateSelectedValues({ attribute, value });
      } else if (filterAttribute.values.length > 0) {
        // if (val) {
        // updateSelectedValues({ attribute: attribute, value: val });
        // }
      }
    }
  };

  const breakpointMatches = useBreakpoint('sm');
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const isMobile = hasMounted ? breakpointMatches : false;

  return isMobile ? (
    <Card className={cn('rounded-md bg-transparent', className?.wrapper)}>
      <Popover open={collapsible ? isOpen : true} onOpenChange={setIsOpen}>
        <PopoverTrigger
          className={cn(
            'flex items-center justify-center gap-2 px-6 py-3.5',
            'ea-attribute-side-dynamic__collapsible-trigger',
            className?.trigger,
            { 'pointer-events-none': !collapsible },
            isOpen ? 'bg-neutral-50' : 'bg-white',
          )}
        >
          <span className={cn(isOpen ? 'text-white' : 'text-primary')}>{name}</span>
          <span
            className={cn(
              'rotate-0 text-primary transition-transform duration-100 ease-linear',
              isOpen && 'rotate-180 text-white',
            )}
          >
            <MdKeyboardArrowDown size={20} />
          </span>
        </PopoverTrigger>
        <PopoverContent
          sideOffset={0}
          align="start"
          className={cn(
            'z-30 flex flex-col items-start justify-start gap-2 rounded-none border-none pl-6 pr-2 pt-6',
            'ea-attribute-side-dynamic__collapsible-content',
          )}
        >
          {attribute.type === AttributeType.CHECKBOX && (
            <AttributeCheckbox
              attribute={attribute}
              updateVariantValues={updateVariantValues}
              className={cn('', 'ea-attribute-side-dynamic__checkbox')}
            />
          )}
          {attribute.type === AttributeType.SIZE && (
            <AttributeCheckbox
              attribute={attribute}
              updateVariantValues={updateVariantValues}
              className={cn('', 'ea-attribute-side-dynamic__size')}
            />
          )}
          {attribute.type === AttributeType.RADIO && (
            <AttributeRadio
              attribute={attribute}
              updateVariantValues={updateVariantValues}
              className={cn('', 'ea-attribute-side-dynamic__radio')}
            />
          )}
          {(attribute.type === AttributeType.COLOR || attribute.type === AttributeType.MULTICOLOR) && (
            <AttributeColor
              attribute={attribute}
              updateVariantValues={updateVariantValues}
              className={cn('', 'ea-attribute-side-dynamic__color')}
            />
          )}
          {attribute.type === AttributeType.SELECT && (
            <AttributeSelect
              attribute={attribute}
              updateVariantValues={updateVariantValues}
              className={cn('', 'ea-attribute-side-dynamic__select')}
            />
          )}
        </PopoverContent>
      </Popover>
    </Card>
  ) : (
    <div className="flex w-full flex-col items-start justify-start gap-6 px-4">
      <span className="font-heading text-lg font-normal uppercase text-foreground">{name}</span>
      {attribute.type === AttributeType.CHECKBOX && (
        <AttributeCheckbox
          attribute={attribute}
          updateVariantValues={updateVariantValues}
          className={cn('', 'ea-attribute-side-dynamic__checkbox')}
        />
      )}
      {attribute.type === AttributeType.SIZE && (
        <AttributeCheckbox
          attribute={attribute}
          updateVariantValues={updateVariantValues}
          className={cn('', 'ea-attribute-side-dynamic__size')}
        />
      )}
      {attribute.type === AttributeType.RADIO && (
        <AttributeRadio
          attribute={attribute}
          updateVariantValues={updateVariantValues}
          className={cn('', 'ea-attribute-side-dynamic__radio')}
        />
      )}
      {(attribute.type === AttributeType.COLOR || attribute.type === AttributeType.MULTICOLOR) && (
        <AttributeColor
          attribute={attribute}
          updateVariantValues={updateVariantValues}
          className={cn('', 'ea-attribute-side-dynamic__color')}
        />
      )}
      {attribute.type === AttributeType.SELECT && (
        <AttributeSelect
          attribute={attribute}
          updateVariantValues={updateVariantValues}
          className={cn('', 'ea-attribute-side-dynamic__select')}
        />
      )}
    </div>
  );
}

export function AttributeSideDynamicAvailability({
  name,
  availabilities,
  collapsible = true,
  className,
}: {
  name: string;
  availabilities?: any[] | null; // Replace AvailabilityType with the actual type
  collapsible?: boolean;
  className?: {
    wrapper?: string;
    trigger?: string;
  };
}) {
  const { isOpen, setIsOpen } = useDisclosure(collapsible ? false : true);

  const filter = useCategoryStore(state => state.filter.availabilities);
  const setFilter = useCategoryStore(state => state.setFilter);

  const updateAvailabilityFilter = (availabilityId: number) => {
    setFilter({ availability: availabilityId });
  };

  const breakpointMatches = useBreakpoint('sm');
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const isMobile = hasMounted ? breakpointMatches : false;

  return isMobile ? (
    <Card className={cn('rounded-md bg-transparent', className?.wrapper)}>
      <Popover open={collapsible ? isOpen : true} onOpenChange={setIsOpen}>
        <PopoverTrigger
          className={cn(
            'flex items-center justify-center gap-2 px-6 py-3.5',
            'ea-attribute-side-dynamic__collapsible-trigger',
            className?.trigger,
            { 'pointer-events-none': !collapsible },
            isOpen ? 'bg-neutral-50' : 'bg-white',
          )}
        >
          <span className={cn(isOpen ? 'text-white' : 'text-primary')}>{name}</span>
          <span
            className={cn(
              'rotate-0 text-primary transition-transform duration-100 ease-linear',
              isOpen && 'rotate-180 text-white',
            )}
          >
            <MdKeyboardArrowDown size={20} />
          </span>
        </PopoverTrigger>
        <PopoverContent
          sideOffset={0}
          align="start"
          className={cn(
            'z-30 flex flex-col items-start justify-start gap-2 rounded-none border-none pl-6 pr-2 pt-6',
            'ea-attribute-side-dynamic__collapsible-content',
          )}
        >
          <AttributeCheckboxAvailability
            availabilities={availabilities}
            updateAvailabilityFilter={updateAvailabilityFilter}
            className={cn('', 'ea-attribute-side-dynamic__availability')}
          />
        </PopoverContent>
      </Popover>
    </Card>
  ) : (
    <div className="flex w-full flex-col items-start justify-start gap-6 px-4">
      <span className="font-heading text-lg font-normal uppercase text-foreground">{name}</span>
      <AttributeCheckboxAvailability
        availabilities={availabilities}
        updateAvailabilityFilter={updateAvailabilityFilter}
        className={cn('', 'ea-attribute-side-dynamic__availability')}
      />
    </div>
  );
}

export function AttributeCheckboxAvailability({
  availabilities,
  updateAvailabilityFilter,
  className = '',
}: {
  availabilities?: any[] | null;
  updateAvailabilityFilter: (availabilityId: number) => void;
  className?: string;
}) {
  const filter = useCategoryStore(state => state.filter.availabilities);

  const sortedAvailabilities = [...(availabilities ?? [])].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0),
  );

  return (
    <div className={cn('flex flex-col gap-4', 'ea-availability-checkbox', className)}>
      {availabilities?.length ? (
        sortedAvailabilities.map(availability => (
          <div
            key={`availability-${availability.id}`}
            className={cn(
              'mr-2 flex flex-row items-center space-x-2',
              'ea-availability-checkbox__input-wrapper',
            )}
          >
            <Checkbox
              id={`availability-${availability.id}`}
              name={`availability-${availability.id}`}
              className={cn('', 'ea-availability-checkbox__input')}
              checked={filter.includes(availability.id)}
              onCheckedChange={() => updateAvailabilityFilter(availability.id)}
            />
            <Label
              htmlFor={`availability-${availability.id}`}
              className={cn('', 'ea-availability-checkbox__label')}
            >
              {availability.name}
            </Label>
          </div>
        ))
      ) : (
        <p className={cn('text-sm', 'ea-availability-checkbox__no-attribute-text')}>
          No availabilities found.
        </p>
      )}
    </div>
  );
}
